import { ReactNode, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import EmailDialog from "../../../components/Approval/EmailDialog";
import { useApiClient } from "../../../../data/clients/ApiClient";
import {
  Design,
  DesignState,
  getDesignState,
} from "../../../../core/models/entities/Design";
import { PageQuery } from "../../../../utils/models/pageQuery";
import { Organization } from "../../../../core/models/entities/Organization";
import { QueryChanger } from "../../../shared/hooks/usePageQuery";
import { TableData } from "../../../components/GenericTable/FunctionalList";
import { useProfileContext } from "../../../../contexts/ProfileContext";
import { useNavigate } from "react-router-dom";
import { PrimaryButton } from "../../../components/Buttons/PrimaryButton";
import { designColumnsConfig } from "../functions/designColumnsConfig";
import { DesignRowItem } from "../components/design-list-item.component";
import { Profile } from "../../../../core/models/entities/Profile";
import {
  FilterRecipe,
  FilterChangeEvent,
} from "../../../components/GenericTable/SubComponents/TableHeader/ListHeader";
import { Role } from "../../../../core/models/entities/Role";
import { organizationFilterField } from "../../../shared/const/organizationFilterField";
import { Manufacturer } from "../../../../core/models/entities/Manufacturer";
import { Style } from "../../../../core/models/entities/Style";
import {
  TransferLogoType,
  TransferMethod,
} from "../../../../core/models/entities/LogoOptions";
import {
  GetOverallApprovalStatus,
  hasUserPendingApprovalStatus,
  LogoState,
} from "../../../../core/models/entities/BaseLogo";
import { ApprovalStatus } from "../../../../core/models/entities/Approval";
import { DesignStatus } from "../../../../core/models/entities/enums/DesignStatus";
import { useDesignActionHandler } from "../hooks/useDesignActionHandler";
import { useMultiSelectStateMachine } from "../../../shared/hooks/useMultiSelectStateMachine";
import { useLogoContext } from "../../design-editor2/contexts/logo-options.context";
import {
  AddToCartIcon,
  TrashcanCrossIcon,
} from "../../../../resources/icons/Icons";
import { enviroment } from "../../../../enviroment";
import {
  DESIGN_METHOD_FILTER_KEY,
  DESIGN_SCREEN_PRINT_METHOD_FILTER_VALUE,
  getMethodLabel,
  getStatusLabel,
  getTypeLabel,
  isLogoListI18nOn,
  mergeScreenPrintMethods,
  rewriteScreenPrintMethodFilter,
} from "../../../../utils/functions/logoListI18n";
import {
  DESIGNS_NONE_ORDERABLE_KEY,
  noneOrderableDesigns,
} from "../../../../utils/functions/orderability";
import {
  countLabel,
  RequireSelection,
} from "../../../components/RequireSelection/RequireSelection";
import { MultiDeleteDialogs } from "../components/multi-delete-dialogs.component";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faDownload,
  faFolder,
  faPlus,
  faShare,
  faEnvelope,
} from "@fortawesome/free-solid-svg-icons";
import {
  useErrorToast,
  useSuccessToast,
  withToasts,
} from "../../../components/Toast/Toast";
import { useRepositoriesContext } from "../../../../contexts/RepositoriesContext";
import { DesignFolder } from "../../../../core/models/entities/DesignFolder";
import {
  DESIGN_DRAG_MIME,
  FolderBar,
  flattenFolders,
  UNFILED_FOLDER_ID,
} from "../../../components/FolderBar/folder-bar.component";
import { SecondaryButton } from "../../../components/Buttons/SecondaryButton";
import { LogoPdfGenerator } from "../../../../utils/functions/pdf-generator.service";
import { usePdfDownloadWithSharpGate } from "../../../../hooks/usePdfDownloadWithSharpGate";
import { Menu, MenuItem, Tooltip } from "@mui/material";
import { useTranslation } from "react-i18next";

export interface DesignRow extends Design {
  checked: boolean | undefined;
}

export type DesignClickCallback = (design: Design) => void;

export function useFunctionalListDesignViewModel(
  onSelectDesign: DesignClickCallback,
  onApprove: DesignClickCallback,
  onReject: DesignClickCallback,
  lastPage: number,
  query: PageQuery,
  customers: Organization[],
  isPageLoading: boolean,
  vendors: Organization[] | undefined,
  queryChanger: QueryChanger,
  manufacturers: Manufacturer[],
  styles: Style[],
  methods: TransferMethod[],
  onShowRejectionNote: DesignClickCallback,
  // openInitApproval: (design: Design) => void,
  designs?: Design[],
  updatingDesignId?: number,
  loadingPrices?: boolean,
  // keepPreviousData indicators (see design-list.screen.tsx): forwarded to
  // FunctionalList so stale rows are dimmed while a new query loads.
  isFetching?: boolean,
  isPreviousData?: boolean,
): TableData<Design> & { folderBar?: ReactNode } {
  const { t } = useTranslation();
  const { profile, role, organizationId } = useProfileContext();
  const {
    handleAddToCart,
    handleAddItemsToCart,
    handleDeleteDesign,
    handlePlaceVerdict,
    handleGoToUpdateDesign,
    handleRejectAndDelete,
  } = useDesignActionHandler();
  const { generatePublicKeys, sendMultiApprovalEmail, ...apiClient } =
    useApiClient();
  const { designFolderRepository } = useRepositoriesContext();
  const queryClient = useQueryClient();
  const [deleted, setDeleted] = useState<number[]>([]);

  const navigate = useNavigate();

  // --- Design folders (fase 1: Vendor/Admin only; customers see the list
  // exactly as before - no folder bar, no folders request) ---
  const isFolderUser = role === Role.Admin || role === Role.Vendor;
  const { data: folders } = designFolderRepository.useFolders(
    isFolderUser ? organizationId : undefined,
  );

  // The folder a user is standing in IS a list filter: it flows through the
  // existing filterByField/filterByValue plumbing (pageQuery.ts), so backend,
  // caching and keepPreviousData work unchanged. No filter entry = root
  // ("All designs", the unchanged default view); value '0' = Unfiled.
  const folderFilterField = "folder.id";
  const activeFolderFilter = query.filterBy?.find(
    (f) => f.field === folderFilterField,
  );
  const currentFolderId = activeFolderFilter
    ? Number(activeFolderFilter.value)
    : null;

  function navigateToFolder(folderId: number | null) {
    const otherFilters = (query.filterBy ?? []).filter(
      (f) => f.field !== folderFilterField,
    );
    const newFilters =
      folderId === null
        ? otherFilters
        : [...otherFilters, { field: folderFilterField, value: String(folderId) }];
    queryChanger.setPageQuery({
      ...query,
      filterBy: newFilters,
      page: { ...query.page, index: 1 },
    });
  }

  // ListHeader owns its own filter state and replaces the whole filter list on
  // every change - it never knows about the folder filter (folder navigation
  // lives outside the filter bar). Re-attach the current folder filter here so
  // applying/clearing header filters never kicks the user out of a folder.
  function commitFilters(activeFilters: FilterChangeEvent["activeFilters"]) {
    const withoutFolder = activeFilters.filter(
      (f) => f.key !== folderFilterField,
    );
    const merged = activeFolderFilter
      ? [
          ...withoutFolder,
          { key: folderFilterField, value: activeFolderFilter.value },
        ]
      : withoutFolder;
    queryChanger.handleFilterChange({ activeFilters: merged });
  }

  const [moveMenuAnchor, setMoveMenuAnchor] = useState<null | HTMLElement>(
    null,
  );
  const [isMoving, setIsMoving] = useState(false);

  async function moveDesignsToFolder(
    designIds: number[],
    target: DesignFolder | null,
  ) {
    if (!organizationId || designIds.length === 0 || isMoving) return;
    setIsMoving(true);
    try {
      await designFolderRepository.moveDesignsToFolder(
        organizationId,
        designIds,
        target?.id ?? null,
      );
      useSuccessToast(
        t("designListStyle.movedDesignsToFolder", {
          count: designIds.length,
          target: target ? target.name : t("designListStyle.unfiled"),
        }),
      );
      resetSelection();
    } catch {
      useErrorToast(t("designListStyle.moveDesignsFailed"));
    } finally {
      setIsMoving(false);
    }
  }

  function handleDesignDragStart(
    design: Design,
    e: React.DragEvent<HTMLDivElement>,
  ) {
    // Dragging a row that is part of the current selection drags the whole
    // selection; dragging an unselected row drags just that design.
    const selectedIds = Object.values<Design>(selectedData).map((d) => d.id);
    const ids = selectedIds.includes(design.id) ? selectedIds : [design.id];
    e.dataTransfer.setData(DESIGN_DRAG_MIME, JSON.stringify(ids));
    e.dataTransfer.setData("text/plain", ids.join(","));
    e.dataTransfer.effectAllowed = "move";
  }

  async function handleCreateFolder(name: string) {
    if (!organizationId) return;
    try {
      await designFolderRepository.createFolder(
        organizationId,
        name,
        currentFolderId === UNFILED_FOLDER_ID ? null : currentFolderId,
      );
    } catch (error) {
      useErrorToast(t("designListStyle.createFolderFailed"));
      throw error; // FolderBar keeps the input open for a retry
    }
  }

  async function handleRenameFolder(folder: DesignFolder, newName: string) {
    if (!organizationId) return;
    try {
      await designFolderRepository.renameFolder(
        organizationId,
        folder.id,
        newName,
      );
    } catch (error) {
      useErrorToast(t("designListStyle.renameFolderFailed"));
      throw error; // FolderBar keeps the input open for a retry
    }
  }

  async function handleDeleteFolder(folder: DesignFolder) {
    if (!organizationId) return;
    try {
      await designFolderRepository.deleteFolder(organizationId, folder.id);
    } catch {
      // Backend answers 409/400 when the folder still contains designs.
      useErrorToast(t("designListStyle.folderMustBeEmpty"));
    }
  }

  // Re-parent a folder (parentId null = top level). Self/descendant/no-op
  // guards already ran inside the FolderBar.
  async function handleMoveFolder(
    folder: DesignFolder,
    parentId: number | null,
  ) {
    if (!organizationId) return;
    try {
      await designFolderRepository.moveFolder(
        organizationId,
        folder.id,
        parentId,
      );
      const targetName =
        parentId !== null
          ? folders?.find((f) => f.id === parentId)?.name
          : undefined;
      useSuccessToast(
        targetName
          ? t("designListStyle.movedFolderTo", {
              folder: folder.name,
              target: targetName,
            })
          : t("designListStyle.movedFolderToTopLevel", {
              folder: folder.name,
            }),
      );
    } catch {
      useErrorToast(t("designListStyle.moveFolderFailed"));
    }
  }

  // Persist a new sibling order for one level (parentId null = top level).
  // The FolderBar renders the new order optimistically and falls back to the
  // (refetched) server order once this resolves - success or failure.
  async function handleReorderFolders(
    parentId: number | null,
    orderedIds: number[],
  ) {
    if (!organizationId) return;
    try {
      await designFolderRepository.reorderFolders(
        organizationId,
        parentId,
        orderedIds,
      );
    } catch {
      useErrorToast(t("designListStyle.reorderFoldersFailed"));
    }
  }

  function onToCreateDesign() {
    navigate("/design/create");
  }

  const { selectedData, onRowChecked, onMultiSelectRow, resetSelection } =
    useMultiSelectStateMachine<Design>({
      onSelect: onSelectDesign,
    });

  // Email dialog state for multi-select Send Preview
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [approvalEmail, setApprovalEmail] = useState("");

  // Loading state for PDF download
  const [isDownloadingPdf, setIsDownloadingPdf] = useState(false);
  // Flag REACT_APP_PDF_WAIT_SHARP_ENABLED: bounded wait for sharp logo images
  // + a dialog instead of a silently blurred PDF. Flag off = a plain
  // withToasts around the generator (as before) and a null dialog.
  const { downloadPdf, sharpGateDialog } = usePdfDownloadWithSharpGate({
    colorHex: profile?.whiteLabelConfigurations?.colorHex,
  });

  // Loading state for sending designs via email
  const [isSendingEmail, setIsSendingEmail] = useState(false);

  // Menu state
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const openMenu = Boolean(anchorEl);

  // Multi-delete (flag REACT_APP_MULTI_DELETE_ENABLED): dialog step
  // (0 closed / 1 neutral confirm / 2 red final confirm) + run state.
  const [multiDeleteStep, setMultiDeleteStep] = useState<0 | 1 | 2>(0);
  const [isDeletingSelected, setIsDeletingSelected] = useState(false);
  const [multiDeleteProgress, setMultiDeleteProgress] = useState(0);

  function handleShareClick(event: React.MouseEvent<HTMLElement>) {
    setAnchorEl(event.currentTarget);
  }

  function handleShareClose() {
    setAnchorEl(null);
  }

  // Validation function to check if user can perform bulk actions with current selection
  function validateSelectionCount(
    selectedCount: number,
    actionName: string,
  ): boolean {
    if (role !== Role.Admin && selectedCount > 10) {
      useErrorToast(
        t("designListStyle.maxSelectionWarning", { action: actionName }),
      );
      return false;
    }
    return true;
  }

  function addToCart() {
    handleAddItemsToCart(Object.values(selectedData), 10);
  }

  function sendPreview() {
    handleShareClose();
    const selectedCount = Object.values(selectedData)?.length || 0;
    if (
      !validateSelectionCount(selectedCount, t("designListStyle.actionSend")) ||
      isSendingEmail
    ) {
      return;
    }
    setEmailDialogOpen(true);
  }

  async function handleDownloadPdf() {
    handleShareClose();
    const selectedCount = Object.values(selectedData)?.length || 0;
    if (
      !validateSelectionCount(
        selectedCount,
        t("designListStyle.actionDownload"),
      ) ||
      isDownloadingPdf
    ) {
      return;
    }

    setIsDownloadingPdf(true);
    const selectedDesigns = Object.values<Design>(selectedData);
    const vendorLogoUrl = profile?.whiteLabelConfigurations?.logoUrl;

    try {
      await downloadPdf({
        run: (options) =>
          LogoPdfGenerator.generateMultipleDesignsPdf(
            selectedDesigns,
            vendorLogoUrl,
            apiClient,
            options,
          ),
        loadingMessage: t("designListStyle.generatingDesignPdf"),
        successMessage: t("designListStyle.designPdfDownloaded"),
        errorMessage: t("designListStyle.designPdfFailed"),
        // Runs after every PDF that was actually saved - also one started
        // from the sharp-gate dialog. A stopped PDF keeps the selection.
        onDownloaded: resetSelection,
        setRerunBusy: setIsDownloadingPdf,
      });
    } catch (error) {
      // Error is already handled by withToasts
      console.error("Failed to generate design PDF:", error);
    } finally {
      setIsDownloadingPdf(false);
    }
  }

  async function handleSendPreviewWithEmail() {
    const selectedDesigns = Object.values<Design>(selectedData);

    setIsSendingEmail(true);

    async function transaction() {
      // Generate public keys for each selected design using API
      const response = await generatePublicKeys(
        undefined,
        selectedDesigns.map((d) => d.id),
      );
      const keysParam = response.publicKeys.join(",");
      const url = `${window.location.origin}/external-approval/designs?keys=${keysParam}`;

      // Send email with preview URL
      await sendMultiApprovalEmail(
        response.publicKeys,
        "design",
        approvalEmail,
      );

      return response;
    }

    try {
      await withToasts(
        transaction,
        t("designListStyle.sendingEmailWithDesigns"),
        t("designListStyle.emailsSentSuccessfully"),
        t("designListStyle.sendEmailWithDesignsFailed"),
      );
      setEmailDialogOpen(false);
      resetSelection();
    } catch (error) {
      // Error is already handled by withToasts
      console.error("Failed to send preview email:", error);
    } finally {
      setIsSendingEmail(false);
    }
  }

  const center = undefined;

  const selectedCount = Object.values(selectedData)?.length || 0;
  const hasSelectedDraft = Object.values(selectedData).some(
    (d) => d.status === DesignStatus.Draft,
  );
  // Flag designOrderabilityGuardEnabled (#724425): with a selection where
  // not ONE design is orderable (rule §1), "Add to Order (n)" is disabled
  // and explains why, instead of a click that would only skip everything.
  // Mixed selection = button active, the handler filters + toasts. The
  // drafts hint keeps precedence. Flag off = undefined = today's DOM.
  const noneOrderableSelected =
    enviroment.designOrderabilityGuardEnabled &&
    noneOrderableDesigns(Object.values<Design>(selectedData));
  const addToOrderBlockedHint = hasSelectedDraft
    ? t("designListStyle.draftsCannotBeOrdered")
    : noneOrderableSelected
      ? t(DESIGNS_NONE_ORDERABLE_KEY)
      : undefined;

  // Multi-delete rule (Nichlas 20/8): mirror the design POPUP's "Delete
  // design" button — it deletes ANY design regardless of status (the API has
  // no status gate: it soft-deletes and marks the NAV item UDGÅET). So every
  // selected design is deletable; the double confirmation is the safety net.
  // (The earlier draft-only rule mirrored the ROW button — deliberately
  // replaced per Nichlas' instruction "som popuppen, bare flere ad gangen".)
  const deletableSelected = Object.values<Design>(selectedData);
  const skippedForDeleteCount = 0;

  function deleteSelected() {
    handleShareClose();
    if (isDeletingSelected) return;
    if (deletableSelected.length === 0) {
      useErrorToast(t("designListStyle.multiDeleteNoneDeletable"));
      return;
    }
    setMultiDeleteStep(1);
  }

  // Sequential deletion via the SAME endpoint as the row delete
  // (DELETE organizations/{orgId}/designs/{designId}) with per-design
  // try/catch. Calls the ApiClient directly instead of
  // designRepository.deleteDesign so the ["designs"] cache is invalidated
  // ONCE at the end instead of once per design. Successful ids also go into
  // the local `deleted` hide-list (same mechanism as single delete) so rows
  // disappear even where the roundabout serves a stale page.
  async function executeMultiDelete() {
    if (isDeletingSelected) return;
    setIsDeletingSelected(true);
    setMultiDeleteProgress(0);
    const targets = [...deletableSelected];
    const okIds: number[] = [];
    let failedCount = 0;
    for (const design of targets) {
      try {
        await apiClient.deleteDesign(design.customerId, design.id);
        okIds.push(design.id);
      } catch (error) {
        failedCount++;
        console.error(`Failed to delete design ${design.id}:`, error);
      }
      setMultiDeleteProgress(okIds.length + failedCount);
    }
    if (okIds.length > 0) {
      setDeleted((prev) => [...prev, ...okIds]);
    }
    try {
      await queryClient.invalidateQueries({
        queryKey: ["designs"],
        type: "all",
      });
    } catch {
      // The deletions themselves succeeded; a failed refetch heals on the
      // next visit and the hide-list keeps the UI truthful meanwhile.
    }
    if (failedCount === 0) {
      useSuccessToast(
        t("designListStyle.multiDeleteSuccess", { count: okIds.length }),
      );
    } else {
      useErrorToast(
        t("designListStyle.multiDeletePartial", {
          ok: okIds.length,
          failed: failedCount,
        }),
      );
    }
    resetSelection();
    setIsDeletingSelected(false);
    setMultiDeleteStep(0);
  }
  // Flat folder list for the "Move to folder" picker (children indented under
  // their parent, depth-first) - shared with the logo list.
  const folderOptions = folders ? flattenFolders(folders) : [];

  function handleMoveSelected(target: DesignFolder | null) {
    setMoveMenuAnchor(null);
    const ids = Object.values<Design>(selectedData).map((d) => d.id);
    moveDesignsToFolder(ids, target).then();
  }

  const right = (
    <div className="flex flex-wrap justify-end gap-2">
      {isFolderUser && (
        <>
          <RequireSelection count={selectedCount}>
            <SecondaryButton
              width={"fit"}
              className={"px-4"}
              disabled={selectedCount === 0 || isMoving}
              onClick={(e) => setMoveMenuAnchor(e.currentTarget)}
            >
              <FontAwesomeIcon icon={faFolder} />
              <span className={"flex-1"}>
                {isMoving
                  ? t("designListStyle.moving")
                  : countLabel(
                      selectedCount,
                      t("designListStyle.moveSelectedWithCount", {
                        count: selectedCount,
                      }),
                      t("ux.moveSelected"),
                    )}
              </span>
            </SecondaryButton>
          </RequireSelection>
          <Menu
            anchorEl={moveMenuAnchor}
            open={Boolean(moveMenuAnchor)}
            onClose={() => setMoveMenuAnchor(null)}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
          >
            <MenuItem disabled>{t("designListStyle.moveToFolder")}</MenuItem>
            <MenuItem onClick={() => handleMoveSelected(null)}>
              <FontAwesomeIcon icon={faFolder} className="mr-2 w-4" />
              {t("designListStyle.unfiled")}
            </MenuItem>
            {folderOptions.map(({ folder, depth }) => (
              <MenuItem
                key={folder.id}
                sx={{ paddingLeft: `${16 + depth * 16}px` }}
                onClick={() => handleMoveSelected(folder)}
              >
                <FontAwesomeIcon icon={faFolder} className="mr-2 w-4" />
                {folder.name}
              </MenuItem>
            ))}
          </Menu>
        </>
      )}
      <PrimaryButton
        key={"design-create"}
        width={"fit"}
        className={"px-4"}
        onClick={onToCreateDesign}
      >
        <FontAwesomeIcon icon={faPlus} />
        <span className="flex-1">{t("designListStyle.createNewDesign")}</span>
      </PrimaryButton>
      {role !== Role.Admin &&
        (enviroment.uxListFixesEnabled ? (
          // List fixes: the same wrapper as the logo list; the drafts hint
          // still wins once something IS selected.
          <RequireSelection
            count={selectedCount}
            blockedHint={addToOrderBlockedHint}
          >
            <PrimaryButton
              width={"fit"}
              className={"px-4"}
              onClick={addToCart}
              disabled={selectedCount === 0 || addToOrderBlockedHint !== undefined}
            >
              <AddToCartIcon />
              <span className={"flex-1"}>
                {countLabel(
                  selectedCount,
                  t("designListStyle.addToOrderWithCount", {
                    count: selectedCount,
                  }),
                  t("designListStyle.addToOrder"),
                )}
              </span>
            </PrimaryButton>
          </RequireSelection>
        ) : (
          <Tooltip title={addToOrderBlockedHint ?? ""}>
            <span
              onClick={() => {
                if (addToOrderBlockedHint && selectedCount > 0) {
                  useErrorToast(addToOrderBlockedHint);
                }
              }}
            >
              <PrimaryButton
                width={"fit"}
                className={"px-4"}
                onClick={addToCart}
                disabled={selectedCount === 0 || addToOrderBlockedHint !== undefined}
              >
                <AddToCartIcon />
                <span className={"flex-1"}>
                  {t("designListStyle.addToOrderWithCount", {
                    count: selectedCount,
                  })}
                </span>
              </PrimaryButton>
            </span>
          </Tooltip>
        ))}
      <SecondaryButton
        width={
          isDownloadingPdf || isSendingEmail
            ? "fit"
            : selectedCount > 0
              ? "xs"
              : "icon"
        }
        className={"px-4 whitespace-nowrap"}
        onClick={handleShareClick}
        disabled={selectedCount === 0 || isDownloadingPdf || isSendingEmail}
      >
        <FontAwesomeIcon icon={faShare} />
        <span className={"flex-1"}>
          {isDownloadingPdf
            ? t("designListStyle.generating")
            : isSendingEmail
              ? t("designListStyle.sending")
              : selectedCount > 0
                ? `(${selectedCount})`
                : ""}
        </span>
      </SecondaryButton>
      <Menu
        anchorEl={anchorEl}
        open={openMenu}
        onClose={handleShareClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <MenuItem onClick={handleDownloadPdf} disabled={isDownloadingPdf}>
          <FontAwesomeIcon icon={faDownload} className="mr-2 w-4" />
          {t("designListStyle.downloadPdf")}
        </MenuItem>
        <MenuItem onClick={sendPreview} disabled={isSendingEmail || hasSelectedDraft}>
          <FontAwesomeIcon icon={faEnvelope} className="mr-2 w-4" />
          {t("designListStyle.sendViaEmail")}
        </MenuItem>
        {/* Multi-delete (flag): destructive item, visually separated from the
            neutral actions above and styled like the rows' red delete. */}
        {enviroment.multiDeleteEnabled && selectedCount > 0 && (
          <MenuItem
            onClick={deleteSelected}
            disabled={isDeletingSelected}
            sx={{
              color: "#E22F2F",
              borderTop: "1px solid #ECEEF9",
              "&:hover": { backgroundColor: "#FFFAFA" },
            }}
          >
            <span className="mr-2 w-4 flex justify-center">
              <TrashcanCrossIcon />
            </span>
            {t("designListStyle.deleteSelectedWithCount", {
              count: selectedCount,
            })}
          </MenuItem>
        )}
      </Menu>
      {sharpGateDialog}
    </div>
  );

  // Because the entire roundabout for designs doesn't start and end the same place,
  //  we cannot simply ask for a fresh list of designs when deleting one.
  // Instead we use a hacky list of deleted designs to ensure we don't remove designs that were not deleted.
  // Next time the list is rendered, the deleted designs will not be included.
  const handleDeleteAndHide = async (design: Design) => {
    try {
      await handleDeleteDesign(design);
      setDeleted([...deleted, design.id]);
    } catch {
      // Do not mark design as deleted after all.
    }
  };

  // The multi-select checkbox is NEVER disabled by the order guard (flag
  // designOrderabilityGuardEnabled, #724425): the same checkbox drives Send
  // via email (the external approval mail / proof), Download PDF, multi-
  // delete and folder drag, and the dealer must be able to tick AWAITING
  // designs and send a proof to several at once (Nichlas 16/10, after the
  // guard's first prod day had disabled exactly those rows). The guard
  // lives in the bulk "Add to Order (n)" button (disabled + hint when
  // nothing selected is orderable, filter + skip toast otherwise -
  // useDesignActionHandler), the row / popup button and the API.
  function isDesignDisabled(_design: Design): boolean {
    return false;
  }

  const handleFilterChange = (rawEvent: FilterChangeEvent) => {
    // Flag logoListI18nEnabled: the merged "Screen Print" Method option
    // never reaches the API - it becomes typeFilter=DIREK TRYK here (same
    // precedent as the showOnlyOwned rewrite below). Off = same array.
    const event: FilterChangeEvent = {
      ...rawEvent,
      activeFilters: rewriteScreenPrintMethodFilter(
        rawEvent.activeFilters,
        DESIGN_METHOD_FILTER_KEY,
        DESIGN_SCREEN_PRINT_METHOD_FILTER_VALUE,
      ),
    };
    if (profile?.userOrganizationInformations?.role === Role.Vendor) {
      const showOnlyOwned = event.activeFilters.find(
        (f) => f.key === "showOnlyOwned",
      );
      const vendorOrgId =
        profile.userOrganizationInformations.organizationId.toString();

      let newFilters = [...event.activeFilters];

      if (showOnlyOwned && showOnlyOwned.value === true) {
        newFilters = newFilters.filter(
          (f) =>
            f.key !== "showOnlyOwned" &&
            f.key !== organizationFilterField &&
            f.key !== "mainOrganizationId",
        );
        newFilters.push({
          key: "mainOrganizationId",
          value: vendorOrgId,
        });
        commitFilters(newFilters);
        return;
      } else {
        newFilters = newFilters.filter((f) => f.key !== "showOnlyOwned");
        newFilters = newFilters.filter(
          (f) => !(f.key === "mainOrganizationId" && f.value === vendorOrgId),
        );
        commitFilters(newFilters);
        return;
      }
    }
    commitFilters(event.activeFilters);
  };

  return {
    tableTitle: t("design"),
    // The design list is the string-titled list that translates the
    // header's showOnlyOwned key (handleFilterChange above) - so it opts in
    // to the "Only show my designs" checkbox. ListHeader ignores this while
    // flag ordersOwnedFilterFixEnabled is off (today's DOM).
    showOwnedFilter: true,
    columns: designColumnsConfig(role === Role.Admin),
    onRowClick: onMultiSelectRow,
    lastPage: lastPage,
    query: query,
    isLoading: isPageLoading,
    isFetching: isFetching,
    isPreviousData: isPreviousData,
    onNewPage: queryChanger.handleNewPage,
    onNewSort: queryChanger.handleSortByField,
    onFilterChange: handleFilterChange,
    onSearchQueryChange: queryChanger.handleSearchQueryChange,
    onPageSizeChange: queryChanger.handlePageSizeChange,
    renderRow: (columns, data, index) => (
      <DesignRowItem
        design={data}
        columns={columns}
        rowIndex={index}
        onSeeRejectNote={() => onShowRejectionNote(data)}
        onInitiateApproval={() => {}}
        onAddToCart={handleAddToCart}
        onPlaceVerdict={handlePlaceVerdict}
        onDeleteDesign={handleDeleteAndHide}
        onEditDesign={handleGoToUpdateDesign}
        onRejectAndDelete={handleRejectAndDelete}
        onApprove={onApprove}
        onReject={onReject}
        updatingDesignId={updatingDesignId}
        loadingPrices={loadingPrices}
        onDragStartDesign={isFolderUser ? handleDesignDragStart : undefined}
      />
    ),
    getKey: getKey,
    center: center,
    right: right,
    data: designs?.filter((d) => !deleted.includes(d.id)),
    getRowColor: logoRowBgColor,
    filters: buildDesignFilterRecipes(
      profile!,
      methods,
      manufacturers,
      styles,
      customers,
      vendors,
    ),
    multiSelect: true,
    // Rendered by DesignListWrapper ABOVE the list box. Only for Vendor/Admin
    // (fase 1) and only once the folders query has data - customers and the
    // initial loading state see the list exactly as before.
    folderBar:
      isFolderUser && folders ? (
        <FolderBar
          folders={folders}
          currentFolderId={currentFolderId}
          rootLabel={t("designListStyle.allDesigns")}
          getFolderCount={(f) => f.designCount}
          dragMime={DESIGN_DRAG_MIME}
          onNavigate={navigateToFolder}
          onCreateFolder={handleCreateFolder}
          onRenameFolder={handleRenameFolder}
          onDeleteFolder={handleDeleteFolder}
          onMoveFolder={handleMoveFolder}
          onReorderFolders={handleReorderFolders}
          onDropItems={(folder, ids) => moveDesignsToFolder(ids, folder)}
        />
      ) : undefined,
    selectedData: selectedData,
    onRowSelected: onRowChecked,
    isRowDisabled: isDesignDisabled,
    showVendorFilter: true,
    customers: customers,
    storageKey: "design-list-filters",
    component: (
      <>
        <EmailDialog
          open={emailDialogOpen}
          setOpen={setEmailDialogOpen}
          email={approvalEmail}
          onEmailChange={setApprovalEmail}
          onSubmit={handleSendPreviewWithEmail}
        />
        {enviroment.multiDeleteEnabled && (
          <MultiDeleteDialogs
            step={multiDeleteStep}
            deletable={deletableSelected}
            skippedCount={skippedForDeleteCount}
            isDeleting={isDeletingSelected}
            progress={multiDeleteProgress}
            onCancel={() => {
              if (!isDeletingSelected) setMultiDeleteStep(0);
            }}
            onContinue={() => setMultiDeleteStep(2)}
            onConfirm={() => {
              executeMultiDelete().then();
            }}
          />
        )}
      </>
    ),
  };
}

function logoRowBgColor(design: Design) {
  if (design.status === DesignStatus.Draft) {
    return "bg-row-draft";
  }

  switch (GetOverallApprovalStatus(design.approval!)) {
    case ApprovalStatus.Rejected:
      return "bg-row-warn";
    case ApprovalStatus.Approved:
      return "bg-row-standard";
    case ApprovalStatus.Pending:
      return "bg-row-pending";
  }
}

function buildDesignFilterRecipes(
  profile: Profile,
  transferMethods: TransferMethod[],
  manufacturers: Manufacturer[],
  style: Style[],
  customers?: Organization[],
  vendor?: Organization[],
) {
  const { t } = useTranslation();
  const { options } = useLogoContext();
  const filters: FilterRecipe<any>[] = [];

  if (profile.userOrganizationInformations.role >= Role.Admin && vendor) {
    filters.push({
      options: vendor,
      key: organizationFilterField,
      getOptionValue: (org) => org.id,
      getOptionDisplay: (org) => org.name,
      defaultTitle: t("vendors"),
    } as FilterRecipe<Organization>);
  }

  if (profile.userOrganizationInformations.role >= Role.Vendor && customers) {
    /*filters.push({
      options: [
        'N/A'
      ],
      key: 'ownership',
      getOptionValue: (o => o),
      getOptionDisplay: (o => o),
      defaultTitle: 'Ownership'
    } as FilterRecipe<string>)*/

    filters.push({
      options: customers,
      key: organizationFilterField,
      getOptionValue: (org) => org.id,
      getOptionDisplay: (org) => org.name,
      defaultTitle: t("customer"),
    } as FilterRecipe<Organization>);
  }

  filters.push({
    options: [
      LogoState.Approved,
      LogoState.Pending,
      LogoState.Rejected,
      LogoState.Draft,
    ],
    key: "stateFilter",
    getOptionValue: (org) => org as string,
    // Flag logoListI18nEnabled: translated option TEXT only - the value
    // (the API's SortableState name) is untouched (logoListI18n.ts).
    getOptionDisplay: isLogoListI18nOn()
      ? (org) => getStatusLabel(org as string, t)
      : (org) => org as string,
    defaultTitle: t("status"),
  } as FilterRecipe<LogoState>);

  filters.push({
    options: options.transferLogoTypes,
    key: "typeFilter",
    getOptionValue: (o) => o.typeKey,
    getOptionDisplay: isLogoListI18nOn()
      ? (o) => getTypeLabel(o, t)
      : (o) => o.nickname,
    defaultTitle: t("type"),
  } as FilterRecipe<TransferLogoType>);

  filters.push({
    // Flag on: every screen-print variant is ONE option whose value is
    // rewritten to typeFilter=DIREK TRYK in handleFilterChange.
    options: mergeScreenPrintMethods(transferMethods, t),
    key: DESIGN_METHOD_FILTER_KEY,
    getOptionValue: (org) => ":" + org.key + ":",
    getOptionDisplay: isLogoListI18nOn()
      ? (org) => getMethodLabel(org, t)
      : (org) => org.displayName,
    defaultTitle: t("designListStyle.method"),
  } as FilterRecipe<TransferMethod>);

  // Manufacturer filter: backed by the flag-gated 'style.manufacturerId' filter
  // branch on the API. Options come from the list the screen already prefetches
  // (design-list.screen.tsx) - no extra fetch. A dedicated Style dropdown was
  // removed by request: style numbers are found via the main search field, which
  // also matches manufacturer name and style number (the 'style.id' API branch
  // remains for deep links).
  filters.push({
    options: [...manufacturers].sort((a, b) =>
      a.name.localeCompare(b.name, undefined, { sensitivity: "base" }),
    ),
    key: "style.manufacturerId",
    getOptionValue: (org) => org.id,
    getOptionDisplay: (org) => org.name,
    defaultTitle: t("manufacturer"),
  } as FilterRecipe<Manufacturer>);

  return filters;
}

const getKey = (data: Design) => data.id;
